import type { UseFormRegister, Control, FieldErrors } from "react-hook-form";
import type { AdminDestinationListItem, Guide } from "@ckgo2/shared/types";
import { OFFER_CONTENT_KEYS, type OfferContentKey, type OfferPageContent } from "@ckgo2/shared";
import i18n from "@/i18n";
import type { HintVariant } from "@/lib/webHints/types";

export interface PracticalInfo {
  visa?: string;
  currency?: string;
  language?: string;
  timezone?: string;
  electricity?: string;
  vaccination?: string;
}

export interface SocialReel {
  thumbnail?: string;
  thumbnailId?: string | null;
  title: string;
  href?: string;
}

export interface Testimonial {
  quote?: string;
  author?: string;
  authorImage?: string;
  authorImageId?: string | null;
  backgroundImage?: string;
  backgroundImageId?: string | null;
}

export interface VideoPromo {
  title?: string;
  description?: string;
  thumbnail?: string;
  thumbnailId?: string | null;
  /**
   * Web ho nečte (řez 05). Editor ho nezobrazuje, ale ukládá beze změny, aby hodnota
   * v DB zůstala (vize F5.4).
   */
  ctaText?: string;
}

export interface WeatherData {
  bestMonths: number[];
  goodMonths: number[];
  /** Prázdná hodnota = údaj chybí (vnitrozemí nemá teplotu vody). */
  temperatures: Record<string, { air?: number; water?: number }>;
  description?: string;
  /** Per-month weather text keyed by month number ("1"–"12"), plain text. */
  monthlyDescriptions?: Record<string, string>;
  /** Short practical "local tip" shown under Climate, plain text. */
  localTip?: string;
}

/**
 * Redakční obsah nabídkových stránek ve FORMULÁŘI (vize C3). Oproti `OfferContentMap`
 * (kde je každý klíč volitelný) je tady záměrně ÚPLNÝ záznam: react-hook-form skládá
 * cesty polí staticky (`offerContent.<klíč>.intro`), takže volitelný klíč by z nich
 * udělal typovou chybu. Prázdné plochy se ukládají jako `{}` a Zod je propustí.
 */
export type OfferContentForm = Record<OfferContentKey, OfferPageContent>;

/** Výchozí (prázdná) hodnota — všechny čtyři plochy existují, žádná není vyplněná. */
export function emptyOfferContentForm(): OfferContentForm {
  return Object.fromEntries(OFFER_CONTENT_KEYS.map((key) => [key, {}])) as OfferContentForm;
}

export interface DestinationFormData {
  name: string;
  slug: string;
  continent: string;
  parentId: string;
  /** Optional custom text of the country infobox on the place page (vize C5). */
  parentInfoText: string;
  guideId: string;
  latitude: string;
  longitude: string;
  shortDescription: string;
  description: string;
  bestTimeToVisit: string;
  climateInfo: string;
  practicalInfo: PracticalInfo;
  highlights: import("./HighlightsEditor").Highlight[];
  regions: import("./RegionsEditor").Region[];
  cuisine: string;
  activities: string[];
  whyWithUs: import("./WhyWithUsEditor").WhyWithUsItem[];
  additionalContent: string;
  additionalImageId: string | null;
  featuredImageId: string | null;
  galleryImageIds: string[];
  heroVideoUrl: string;
  /** „Odkaz na další videa" (vize F5.2) — ve formuláři řetězec, prázdný se ukládá jako NULL. */
  moreVideosUrl: string;
  socialReels: SocialReel[];
  testimonial: Testimonial;
  videoPromo: VideoPromo;
  weatherData: WeatherData;
  heroTitle: string;
  heading: string;
  faqs: Array<{ topic: string; items: Array<{ question: string; answer: string }> }>;
  offerContent: OfferContentForm;
  /**
   * Inspirace přiřazené téhle destinaci. NENÍ to sloupec destinace — je to druhý pohled
   * na `activities.destinationId`, který API při uložení přemapuje (viz `RelatedTab`).
   */
  inspirationIds: string[];
  metaTitle: string;
  metaDescription: string;
  sortOrder: number;
  isFeatured: boolean;
  // Hidden from all public endpoints when false (pre-seeded countries awaiting content).
  isPublished: boolean;
  // Redakční výběr do nabídky destinací (menu, filtr „Kam", hledač na úvodní stránce).
  // Není to publikace — vypnutá destinace má dál svou stránku i zájezdy.
  showInNav: boolean;
  /** Lokalita se prezentuje jako země (viz `destinations.showAsCountry`). */
  showAsCountry: boolean;
  /** Varianta plánovacího bloku (1–3) z Nastavení → Obsah webu; `null` = výchozí znění. */
  planningBlockVariant: number | null;
  /** Vlastní text plánovacího bloku — přebije variantu. Prázdné se ukládá jako NULL. */
  planningBlockText: string;
  // Default calculation pax (Block 5.1) — prefill for trip calculations on this destination.
  defaultMinPax: number | null;
  autoRouteLeads: boolean;
}

export interface DestinationTabProps {
  register: UseFormRegister<DestinationFormData>;
  control: Control<DestinationFormData>;
  errors: FieldErrors<DestinationFormData>;
  /**
   * Varianta editoru (země / lokalita) podle formuláře: nápovědy a viditelnost polí
   * (`FieldHint`, `fieldVisible`) sledují volbu rodiče hned, ne až po uložení.
   */
  variant: HintVariant;
}

export interface BasicTabProps extends DestinationTabProps {
  /** Volby roletky „Nadřazená destinace", už filtrované a seřazené (`parentDestinationOptions`). */
  parentOptions: AdminDestinationListItem[];
  guides: Guide[];
}

export const CONTINENTS = [
  { value: "asia", label: i18n.t("catalog.continents.asia") },
  { value: "africa", label: i18n.t("catalog.continents.africa") },
  { value: "south-america", label: i18n.t("catalog.continents.southAmerica") },
  { value: "north-america", label: i18n.t("catalog.continents.northAmerica") },
  { value: "oceania", label: i18n.t("catalog.continents.oceania") },
  { value: "europe", label: i18n.t("catalog.continents.europe") },
];
